import React from 'react';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { GRADIENTS } from '../../theme/tokens';
import { auroraTopbarSx } from '../../theme/auroraSurface';

/**
 * @deprecated Use `PageShell` + `StudentAppBar` for all /student/* routes.
 * PageHeader is retained for librarian and admin routes only.
 *
 * PageHeader
 * Sticky-capable page title bar with optional back button and action slot.
 *
 * @param {string} title
 * @param {string} [subtitle]
 * @param {string|() => void} [backTo] - path string or callback for back button
 * @param {ReactNode} [actions]
 * @param {boolean} [sticky=false]
 * @param {'plain'|'gradient'} [variant='plain']
 */
function PageHeader({
  title,
  subtitle,
  backTo,
  actions,
  sticky = false,
  variant = 'plain',
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const stickyAuroraSx = sticky ? auroraTopbarSx(theme) : {};

  const handleBack = () => {
    if (typeof backTo === 'function') {
      backTo();
    } else if (typeof backTo === 'string') {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  const isGradient = variant === 'gradient';

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        ...(sticky
          ? {
              ...stickyAuroraSx,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }
          : {}),
        ...(isGradient
          ? {
              background: isLight ? GRADIENTS.brand : GRADIENTS.navyDark,
            }
          : {}),
      }}
    >
      {backTo != null && (
        <IconButton
          onClick={handleBack}
          size="small"
          aria-label="Go back"
          sx={{
            color: isGradient ? 'rgba(255,255,255,0.9)' : 'text.primary',
            mr: 0.5,
          }}
        >
          <ArrowBack />
        </IconButton>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          noWrap
          color={isGradient ? '#FFFFFF' : 'text.primary'}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            color={isGradient ? 'rgba(255,255,255,0.75)' : 'text.secondary'}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
    </Box>
  );
}

export default PageHeader;
